import { useMediaQuery } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { useLocation, Routes, Route } from "react-router-dom";
import {
    LoadingContext,
    M_StateActions,
    NavigateContext,
} from "src/M_Components";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import TrackingConfig from "./TrackingConfig";
//import TrackingConfigCrud from "./trackingConfigCrud";

/**
 * TrackingPage
 *
 * Responsibility:
 * - Provides the Tracking feature shell that supports two modes:
 *   1) TrackingConfig: list/table mode (Shipments overview / search / follow state)
 *   2) TrackingConfigCrud: CRUD mode (create shipment / start tracking / edit flows)
 *
 * Behavior:
 * - Uses the global dynamic-tab architecture, persisting per-tab state in Redux.
 * - Renames the dynamic tab label on language changes.
 * - Restores state when switching between dynamic tabs.
 */
export default function TrackingPage({
    roles,
    onCreateTab,
    toastMessages,
    handleTabClose,
}) {
    /**
     * Provides global UI loading state and shared application colors.
     */
    const loadingCtx = useContext(LoadingContext);

    /**
     * Provides tab utilities (renameTab) for the current dynamic tab.
     */
    const { renameTab } = useContext(NavigateContext);

    /**
     * Redux dispatch to persist session-like state per dynamic tab key.
     */
    const dispatch = useDispatch();

    /**
     * i18n translation accessor used for tab captions and UI strings.
     */
    const { t } = useTranslation();

    /**
     * Allows reacting to route changes, keeping the current tab state consistent.
     */
    const location = useLocation();

    /**
     * Detects layout constraints for optional responsive behavior.
     * This value is kept for feature parity with other pages.
     */
    const matches = useMediaQuery("(min-width:900px)");

    /**
     * Global per-tab persisted state snapshot (key/value list).
     */
    const M_State = useSelector((state) => state.StateKeyValue);

    /**
     * Current selected dynamic tab id (string). Used to rename and to scope persisted state.
     */
    const selectedTab = useSelector(
        (s) => s.StateKeyValue.find((o) => o.key === "selectedTab")?.value
    );


    /**
     * Local state key that scopes persisted values in Redux.
     * This must follow the current dynamic tab id.
     */
    const [id, setId] = useState(
        () => M_State.find((obj) => obj.key === "selectedTab")?.value || "tracking"
    );

    /**
     * Optional view mode selector:
     * - When used, the current mode can be persisted and restored.
     * - The routing remains the primary source of truth for the rendered content.
     */
    const [menu, setMenu] = useState(1);

    /**
     * Currently selected Shipment (or tracking record).
     * This is intentionally generic and can be shaped by TrackingConfig later.
     */
    const [selectedShipment, setSelectedShipment] = useState(null);

    /**
     * Refresh flag used as a simple toggle for forcing list reloads in child components.
     */
    const [refresh, setRefresh] = useState(false);

    /**
     * Keys persisted per tab. Each key maps to a local state variable.
     */
    const sessionKeys = ["menu", "selectedShipment", "refresh"];

    /**
     * Renames the current dynamic tab when:
     * - The language changes, or
     * - The selected dynamic tab changes.
     *
     * Note:
     * - The translation key should match the i18n resources used by the app.
     * - "Tracking" is used as a stable key for the feature caption.
     */
    useEffect(() => {
        if (!selectedTab) return;

        renameTab(String(selectedTab), t("Tracking"));
    }, [t, selectedTab, renameTab]);

    /**
     * Synchronizes the local "id" with the currently selected dynamic tab key.
     * When switching tabs, loads that tab's persisted state.
     */
    useEffect(() => {
        const newId = M_State.find((obj) => obj.key === "selectedTab")?.value;

        if (newId && newId !== id) {
            setId(newId);
            getSessionStorageData(newId);
        }
    }, [M_State, id]);

    /**
     * Restores local state from the Redux per-tab container.
     * The state shape is expected to be a list of { key, value } entries.
     *
     * Loading behavior:
     * - Enables the global loading spinner during restore.
     * - Disables it after restore is complete.
     *
     * @param {string} sessionID
     * The dynamic tab identifier used to retrieve persisted state.
     */
    const getSessionStorageData = (sessionID) => {
        loadingCtx.setIsLoad(true);

        const session = M_State.find((obj) => obj.key === sessionID)?.value;

        if (session) {
            const m = session.find((x) => x.key === "menu")?.value;
            const ship = session.find((x) => x.key === "selectedShipment")?.value;
            const ref = session.find((x) => x.key === "refresh")?.value;

            if (m) setMenu(m);
            if (ship) setSelectedShipment(ship);
            if (ref !== undefined) setRefresh(ref);
        }

        loadingCtx.setIsLoad(false);
    };

    /**
     * Restores the session state for the current tab when the route changes.
     * This keeps behavior consistent with other pages that use dynamic tabs.
     */
    useEffect(() => {
        getSessionStorageData(id);
    }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

    /**
     * Persists local state into Redux under the current dynamic tab key.
     * This enables:
     * - Tab switching without losing in-page selections
     * - Refresh toggles surviving navigation within the tab
     */
    useEffect(() => {
        const values = { menu, selectedShipment, refresh };
        const sessionData = sessionKeys.map((k) => ({ key: k, value: values[k] }));

        dispatch(M_StateActions.addM_State({ key: id, value: sessionData }));
    }, [id, menu, selectedShipment, refresh, dispatch]);

    /**
     * Renders the Tracking feature shell.
     * - Index route uses TrackingConfig (list/table mode)
     * - "trackingConfigCrud" route uses TrackingConfigCrud (CRUD mode)
     */
    return (
        <div className="divGlobalSettings">
            <div>

                <Routes>
                    <Route
                        index
                        element={
                            <TrackingConfig
                                roles={roles}
                                onCreateTab={onCreateTab}
                                toastMessages={toastMessages}
                                selectedShipment={selectedShipment}
                                setSelectedShipment={setSelectedShipment}
                                refresh={refresh}
                                setRefresh={setRefresh}
                                matches={matches}
                            />
                        }
                    />
                    {/*}
                    <Route
                        path="trackingConfigCrud"
                        element={
                            <TrackingConfigCrud
                                handleTabClose={handleTabClose}
                                toastMessages={toastMessages}
                            />
                        }
                    />*/}
                </Routes>
            </div>
        </div>
    );
}